import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormScriptResponse } from '~/models/Commons ';
import { useReplaceScriptOutputTemplateMutation } from '~/services/script';

interface FormScriptOutputTemplate {
  outputTemplateId: { value: number; label: string } | undefined;
}

export const useFormScriptOutputTemplate = (
  item: FormScriptResponse,
  onCloseModal: () => void,
  refetch: () => void,
  onSuccess?: (title: string, message: string, response: any) => void,
  onError?: (title: string, message: string) => void,
) => {
  const isSubmittingRef = useRef(false);
  const { t } = useTranslation();

  const [replaceScriptOutputTemplate, { isLoading, isError }] =
    useReplaceScriptOutputTemplateMutation();

  const currentOT = item.outputTemplateObj ?? undefined;
  const initialOTValue = currentOT
    ? { value: currentOT.id, label: currentOT.name }
    : undefined;

  const {
    formState: { errors },
    handleSubmit,
    control,
    watch,
  } = useForm<FormScriptOutputTemplate>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      outputTemplateId: initialOTValue,
    },
  });

  const watchedOT = watch('outputTemplateId');

  // Submit enabled only when the selected OT differs from the original
  const isChanged = !!watchedOT && watchedOT.value !== currentOT?.id;

  const submitForm = async (data: FormScriptOutputTemplate) => {
    if (isSubmittingRef.current || isLoading) return;
    if (!data.outputTemplateId) return;

    isSubmittingRef.current = true;

    try {
      const response = await replaceScriptOutputTemplate({
        scriptId: item.id,
        outputTemplateId: data.outputTemplateId.value,
      }).unwrap();

      onSuccess?.(
        t('MessageOperationCompletedSuceesuflly'),
        response?.message || '',
        response,
      );

      refetch();
      onCloseModal();
    } catch (error: any) {
      onError?.(
        `${error?.data?.title} (${error?.data?.status})`,
        error?.data?.detail,
      );
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return {
    handleSubmit,
    control,
    errors,
    isLoading,
    isError,
    isChanged,
    submitForm,
  };
};
